import API from "./api";
import { getEfilingListQueryParams } from "./efilingAccess";

/**
 * Individual e-filing backend contract:
 *
 * GET client/getClientEfiling/:clientId?category=individual
 * GET client/getAllClientEfiling?category=individual
 * POST client/createClientEfiling
 * PATCH client/updateClientEfiling/:efilingId
 * PATCH client/updateClientEfilingStatus/:efilingId
 * PATCH client/updateClientEfilingComment/:efilingId
 *
 * Business e-filing (separate table — do NOT reuse individual columns):
 *
 * GET client/getClientBusinessEfiling/:clientId
 * GET client/getAllClientBusinessEfiling
 * POST client/createClientBusinessEfiling
 * PATCH client/updateClientBusinessEfiling/:businessEfilingId
 *
 * Payment sync (both tables):
 * POST client/syncEfilingPaymentStatusFromInvoice
 */

function unwrapList(res) {
  const body = res?.data;
  if (body?.status === 1 && Array.isArray(body.data)) return body.data;
  if (Array.isArray(body?.data)) return body.data;
  return [];
}

export async function fetchClientEfilings(clientId, category) {
  if (!clientId) return [];
  try {
    if (category === "business") {
      const res = await API.get(`client/getClientBusinessEfiling/${clientId}`);
      return unwrapList(res);
    }
    const res = await API.get(`client/getClientEfiling/${clientId}`, {
      params: { category: "individual" },
    });
    return unwrapList(res);
  } catch (_e) {
    return [];
  }
}

export async function createClientEfiling(payload) {
  if (payload?.category === "business") {
    const { category: _category, ...businessPayload } = payload;
    return API.post(`client/createClientBusinessEfiling`, businessPayload);
  }
  return API.post(`client/createClientEfiling`, payload);
}

export async function fetchAllClientEfilings(category, filters = {}) {
  try {
    if (category === "business") {
      const params = getEfilingListQueryParams({ ...filters });
      const res = await API.get(`client/getAllClientBusinessEfiling`, {
        params,
      });
      return unwrapList(res);
    }
    const params = getEfilingListQueryParams({
      category: "individual",
      ...filters,
    });
    const res = await API.get(`client/getAllClientEfiling`, {
      params,
    });
    return unwrapList(res);
  } catch (_e) {
    return [];
  }
}

export async function updateClientEfilingStatus(efilingId, filing_status) {
  return API.patch(`client/updateClientEfilingStatus/${efilingId}`, {
    filing_status,
  });
}

export async function updateClientEfilingComment(efilingId, other_comments) {
  return API.patch(`client/updateClientEfilingComment/${efilingId}`, {
    other_comments,
  });
}

export async function updateClientEfiling(efilingId, payload) {
  if (payload?.category === "business") {
    const { category: _category, ...businessPayload } = payload;
    return API.patch(
      `client/updateClientBusinessEfiling/${efilingId}`,
      businessPayload,
    );
  }
  return API.patch(`client/updateClientEfiling/${efilingId}`, payload);
}

export async function createClientBusinessEfiling(payload) {
  return API.post(`client/createClientBusinessEfiling`, payload);
}

export async function updateClientBusinessEfiling(businessEfilingId, payload) {
  return API.patch(
    `client/updateClientBusinessEfiling/${businessEfilingId}`,
    payload,
  );
}

/**
 * When an invoice is marked Paid, keep linked e-filing row(s) payment_status in sync.
 * Prefers dedicated backend endpoint; falls back to fetching client e-filings and patching matches.
 */
export async function syncEfilingPaymentStatusFromInvoice({
  clientId,
  invoiceNo,
  clientInvoiceId,
  paymentStatus,
} = {}) {
  const status = String(paymentStatus || "").trim();
  if (!status) return { synced: 0 };

  try {
    const res = await API.post(`client/syncEfilingPaymentStatusFromInvoice`, {
      client_id: clientId || null,
      client_invoice_id: clientInvoiceId || null,
      invoice_no: invoiceNo || null,
      payment_status: status,
    });
    if (res?.data?.status === 1) {
      return {
        synced: Number(res?.data?.data?.updated ?? 1) || 1,
        via: "api",
      };
    }
  } catch (_e) {
    /* endpoint may not exist yet — use fallback */
  }

  if (!clientId) return { synced: 0 };

  let synced = 0;
  const invNo = String(invoiceNo || "").trim();
  const invId =
    clientInvoiceId !== undefined && clientInvoiceId !== null
      ? String(clientInvoiceId).trim()
      : "";

  for (const category of ["individual", "business"]) {
    const rows = await fetchClientEfilings(clientId, category);
    for (const row of rows || []) {
      const rowNo = String(row?.invoice_no ?? row?.invoiceNo ?? "").trim();
      const rowInvId = String(
        row?.client_invoice_id ?? row?.clientInvoiceId ?? "",
      ).trim();
      const matchesId = Boolean(invId && rowInvId && invId === rowInvId);
      const matchesNo = Boolean(invNo && rowNo && invNo === rowNo);
      if (!matchesId && !matchesNo) continue;

      const efilingId =
        row?.efiling_id ??
        row?.business_efiling_id ??
        row?.businessEfilingId ??
        row?.id ??
        row?.efilingId;
      if (!efilingId) continue;
      try {
        const patch =
          category === "business"
            ? {
                category: "business",
                payment_status: status,
                invoice_status: status,
              }
            : { payment_status: status };
        await updateClientEfiling(efilingId, patch);
        synced += 1;
      } catch (_err) {
        /* ignore single-row failures */
      }
    }
  }

  return { synced, via: "fallback" };
}
